import { useCallback } from "react";
import {
  useUpdateLessonCompleteStatusMutation,
  useUpdateSectionCompleteStatusMutation,
  useHeaUpdateLessonStatusMutation,
  SectionFragment,
  SectionFragmentDoc,
  useSllUpdateLessonCompleteStatusMutation,
  useUpdateUnitStudyReviewStatusMutation,
  SllLesson,
} from "@api/graphql-codegen";
import { createPrettyURL, isHomeEDApp } from "@constants";
import { resolveStandaloneMixpanelLessonType } from "@src/utils/mixpanelLessonType";
import { resolveUnitMixpanelLessonType } from "@src/utils/mixpanelLessonType";
import {
  normalizeMixpanelProgramType,
  resolveMixpanelProgramType,
} from "@src/utils/mixpanelProgramType";
import { useAnalytics, Events } from "@hooks/useAnalytics";
import { LessonStatus } from "@src/types";
import { useUser } from "./useUser";
import { logger } from '../utils/logger';

export const useMarkLessonComplete = (
  onComplete?: () => void,
  goBack?: () => void,
  section?: SectionFragment
) => {
  const { trackEvent } = useAnalytics();
  const [user] = useUser();
  const [heaUpdateLessonComplete, { loading: heaLoading }] =
    useHeaUpdateLessonStatusMutation({
      onError(e) {
        logger.debug(`[e] useHeaUpdateLessonStatusMutation -`, e);
      },
    });

  const [updateLessonComplete, { loading: lessonLoading }] =
    useUpdateLessonCompleteStatusMutation({
      onError(e) {
        logger.debug(`[e] - useUpdateLessonCompleteStatusMutation`, e);
      },
    });

  const [updateSectionComplete, { loading: sectionLoading }] =
    useUpdateSectionCompleteStatusMutation({
      onError(e) {
        logger.debug(`[e] - useUpdateSectionCompleteStatusMutation`, e);
      },
      update(cache, res, { variables }) {
        const d = res?.data?.updateSectionCompleteStatus;
        if (d && section?.id) {
          cache.updateFragment(
            {
              id: `Section:${section?.id}`,
              fragment: SectionFragmentDoc,
              fragmentName: "Section",
            },
            (l) => ({
              ...l,
              status: variables?.input.status || LessonStatus.Completed,
            })
          );
        }
      },
    });

  const [sllUpdateLessonComplete, { loading: sllLessonLoading }] =
    useSllUpdateLessonCompleteStatusMutation({
      onError(e) {
        logger.debug(`[e]`, e);
      },
    });

  const [updateUnitStudyReviewStatus, { loading: studyReviewLoading }] =
    useUpdateUnitStudyReviewStatusMutation({
      onError(e) {
        logger.debug(`[e] - useUpdateUnitStudyReviewStatusMutation`, e);
      },
    })

  const markLessonComplete = useCallback(
    async (
      unitId: string,
      lessonId: string,
      studyPlanId?: string,
      trackData?: {
        unit_name?: string;
        unit_id?: string;
        unit_unique_name?: string;
        program_name?: string;
        course_name?: string;
        course_id?: string;
        lesson_name?: string;
        lesson_id?: string;
        lesson_position?: number;
        study_plan_id?: string;
        study_plan_name?: string;
        section_id?: string;
      },
      isOnlyNotCompletedLesson?: boolean,
      unitProgress?: number | null,
      isChild?: boolean
    ) => {
      try {
        if (isHomeEDApp) {
          if (studyPlanId) {
            await heaUpdateLessonComplete({
              variables: {
                input: {
                  studyplanId: studyPlanId,
                  unitId,
                  lessonId,
                  status: isChild
                    ? LessonStatus.Completed
                    : LessonStatus.Confirmed,
                  studyPlanName: isChild
                    ? trackData?.study_plan_name
                    : undefined,
                  lessonName: isChild ? trackData?.lesson_name : undefined,
                  unitName: isChild ? trackData?.unit_name : undefined,
                },
              },
            });
            const updatedTrackData = {
              ...trackData,
              study_plan_id: studyPlanId,
              lesson_id: lessonId,
              unit_id: unitId,
              grade: trackData?.course_name || "",
              lessonType: resolveUnitMixpanelLessonType({
                unitName: trackData?.unit_name,
                unitId,
                unitUniqueName: trackData?.unit_unique_name,
              }),
              programType: resolveMixpanelProgramType({
                programName: trackData?.program_name,
                courseName: trackData?.course_name,
                unitName: trackData?.unit_name,
              }),
            };
            trackEvent(Events.actions.lessonCompleted, { ...updatedTrackData, type: 'Unit' });
            if (isOnlyNotCompletedLesson) {
              trackEvent(
                Events.actions.allLessonsCompletedInUnit, {
                  ...updatedTrackData,
                  type: 'Unit',
                }
              );
            }
          }
        } else {
          if (section?.id) {
            await updateSectionComplete({
              variables: {
                input: {
                  sectionId: section?.id,
                  unitId,
                  lessonId,
                  status: LessonStatus.Completed,
                },
              },
            });
            trackEvent(Events.actions.sectionCompleted, {
              ...trackData,
              schoolId: user?.schools?.[0]?.id,
              schoolName: user?.schools?.[0]?.name,
              userId: user?.id,
              userName: `${user?.firstName} + ' ' + ${user?.lastName}`
            });
            if (
              section?.name &&
              section?.name.toLowerCase() === "exit ticket"
            ) {
              await updateLessonComplete({
                variables: {
                  input: {
                    unitId,
                    lessonId,
                    status: LessonStatus.Completed,
                  },
                },
              });
              trackEvent(Events.actions.lessonCompleted, {
                ...trackData,
                course_name:
                  trackData?.unit_name === "Educator Onboarding"
                    ? "Educator Onboarding"
                    : (trackData?.unit_name === "Educator Wellbeing" ? "Educator Wellbeing" : trackData?.course_name),
                lessonType: resolveUnitMixpanelLessonType({
                  unitName: trackData?.unit_name,
                  unitId: trackData?.unit_id || unitId,
                  unitUniqueName: trackData?.unit_unique_name,
                }),
                programType: resolveMixpanelProgramType({
                  programName: trackData?.program_name,
                  courseName: trackData?.course_name,
                  unitName: trackData?.unit_name,
                }),
                type: 'Unit',
                schoolId: user?.schools?.[0]?.id,
                schoolName: user?.schools?.[0]?.name,
                userId: user?.id,
                userName: `${user?.firstName} ${user?.lastName}`,
              });
            }
          } else {
            await updateLessonComplete({
              variables: {
                input: {
                  unitId,
                  lessonId,
                  status: LessonStatus.Completed,
                },
              },
            });

            const courseName = trackData?.unit_name === "Educator Onboarding" ? "Educator Onboarding" : trackData?.unit_name === "Educator Wellbeing" ? "Educator Wellbeing" : trackData?.course_name;
            const updatedTrackData = {
              unit_name: trackData?.unit_name || "",
              unit_id: trackData?.unit_id || "",
              course_name: courseName || "",
              course_id: trackData?.course_id || "",
              lesson_name: trackData?.lesson_name || "",
              lesson_id: trackData?.lesson_id || "",
              lesson_position: trackData?.lesson_position || 0,
              grade: courseName || "",
              lessonType: resolveUnitMixpanelLessonType({
                unitName: trackData?.unit_name,
                unitId: trackData?.unit_id || unitId,
                unitUniqueName: trackData?.unit_unique_name,
              }),
              programType: resolveMixpanelProgramType({
                programName: trackData?.program_name,
                courseName,
                unitName: trackData?.unit_name,
              }),
              schoolId: user?.schools?.[0]?.id,
              schoolName: user?.schools?.[0]?.name,
              userId: user?.id,
              userName: `${user?.firstName} ${user?.lastName}`,
            };
            if (trackData?.unit_name === "Educator Onboarding" || trackData?.unit_name === "Educator Wellbeing") {
              trackEvent(Events.actions.lessonCompleted, {...updatedTrackData, type: "Unit"})
            } else {
              trackEvent(Events.actions.lessonCompleted, {...updatedTrackData, type: "Unit"})
            }
          }
          if (isOnlyNotCompletedLesson) {
            if (!section?.id) {
              trackEvent(Events.actions.allLessonsCompletedInUnit, {
                ...trackData,
                schoolId: user?.schools?.[0]?.id,
                schoolName: user?.schools?.[0]?.name,
                userId: user?.id,
                userName: `${user?.firstName} ${user?.lastName}`,
              });
            }
          } else if (!unitProgress) {
            trackEvent(Events.actions.startUnit, {
              ...trackData,
              schoolId: user?.schools?.[0]?.id,
              schoolName: user?.schools?.[0]?.name,
              userId: user?.id,
              userName: `${user?.firstName} + ${user?.lastName}`
            });
          }
        }
        if (onComplete) {
          onComplete();
        }
      } catch (e) {
        logger.debug(`[e] markLessonComplete - `, e);
      }
      if (goBack && !isChild) {
        goBack();
      }
    },
    [
      user,
      goBack,
      heaUpdateLessonComplete,
      onComplete,
      section,
      trackEvent,
      updateLessonComplete,
      updateSectionComplete,
    ]
  );

  const lessonNotStarted = useCallback(
    async (unitId: string, lessonId: string) => {
      try {
        if (section?.id) {
          await updateSectionComplete({
            variables: {
              input: {
                sectionId: section?.id,
                unitId,
                lessonId,
                status: LessonStatus.NotStarted,
              },
            },
          });
        } else {
          await updateLessonComplete({
            variables: {
              input: {
                unitId,
                lessonId,
                status: LessonStatus.NotStarted,
              },
            },
          });
        }
      } catch (e) {
        logger.debug(`[e] lessonNotStarted - `, e);
      }
      if (goBack) {
        goBack();
      }
    },
    [goBack, section?.id, updateLessonComplete, updateSectionComplete]
  );

  const heUpdateLessonInProgress = useCallback(
    async (unitId: string, lessonId: string, studyPlanId: string) => {
      if (!isHomeEDApp) {
        return;
      }
      try {
        await heaUpdateLessonComplete({
          variables: {
            input: {
              studyplanId: studyPlanId,
              unitId,
              lessonId,
              status: LessonStatus.InProgress,
            },
          },
        });
      } catch (e) {
        logger.debug(`[e] heaUpdateLessonInProgress - `, e);
      }
      if (goBack) {
        goBack();
      }
    },
    [goBack, heaUpdateLessonComplete]
  );

  const sllMarkLessonComplete = useCallback(
    async (l: SllLesson, programName?: string | undefined) => {
      try {
        await sllUpdateLessonComplete({
          variables: {
            input: {
              lessonId: l.id,
              status: LessonStatus.Completed,
            },
          },
        });
        trackEvent(Events.actions.lessonCompleted, {
          lesson_id: l.id,
          lesson_name: l.name,
          grade: l.ageGroup || l.gradeLevel,
          skill: l.skill,
          ...(l.collections && l.collections.length && {
            collection_name: l.collections.filter(c => c?.collectionType === 'Theme')[0]?.name,
          }),
          ...(l.multiCollections && l.multiCollections.length && {
            program_name: normalizeMixpanelProgramType(
              l.multiCollections[0]?.programType,
            ),
            multi_collection_name: l.multiCollections.map(mc => mc?.name),
          }),
          programType: resolveMixpanelProgramType({
            programName: programName || l.multiCollections?.[0]?.programType,
            courseName: l.multiCollections?.[0]?.programType,
          }),
          lessonType: resolveStandaloneMixpanelLessonType({
            lesson: { uniqueName: l.uniqueName, lessonType: l.lessonType },
            programName,
          }),
          originalURL: createPrettyURL(`standalone/${l.uniqueName}`),
          type: 'Standalone',
          schoolId: user?.schools?.[0]?.id,
          schoolName: user?.schools?.[0]?.name,
          userId: user?.id,
          userName: `${user?.firstName} ${user?.lastName}`,
        });

        if (onComplete) {
          onComplete();
        }
      } catch (e) {
        logger.debug(`[e] markLessonComplete - `, e);
      }
      if (goBack) {
        goBack();
      }
    },
    [goBack, sllUpdateLessonComplete, onComplete, trackEvent, user]
  );

  const sllLessonNotStarted = useCallback(
    async (lessonId: string) => {
      try {
        await sllUpdateLessonComplete({
          variables: {
            input: {
              lessonId,
              status: LessonStatus.NotStarted,
            },
          },
        });
      } catch (e) {
        logger.debug(`[e] lessonNotStarted - `, e);
      }
      if (goBack) {
        goBack();
      }
    },
    [goBack, sllUpdateLessonComplete]
  );

  const markStudyReviewComplete = useCallback(
    async (unitId: string) => {
      try {
        await updateUnitStudyReviewStatus({
          variables: {
            input: {
              unitId,
              status: LessonStatus.Completed,
            },
          },
        });
        if (onComplete) {
          onComplete();
        }
      } catch (e) {
        logger.debug(`[e] markLessonComplete - `, e);
      }
      if (goBack) {
        goBack();
      }
    },
    [goBack, updateUnitStudyReviewStatus, onComplete]
  );

  const studyReviewNotStarted = useCallback(
    async (unitId: string) => {
      try {
        await updateUnitStudyReviewStatus({
          variables: {
            input: {
              unitId,
              status: LessonStatus.NotStarted,
            },
          },
        });
      } catch (e) {
        logger.debug(`[e] lessonNotStarted - `, e);
      }
      if (goBack) {
        goBack();
      }
    },
    [goBack, updateUnitStudyReviewStatus]
  );

  return {
    markLessonComplete,
    lessonNotStarted,
    heUpdateLessonInProgress,
    sllMarkLessonComplete,
    sllLessonNotStarted,
    markStudyReviewComplete,
    studyReviewNotStarted,
    loading: heaLoading || lessonLoading || sectionLoading || sllLessonLoading || studyReviewLoading,
  };
};
